import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SftGlobalData from '../../library/SftGlobalData';

// 13-digit version (YYYYMMDDHHMM + digit) as a readable date.
const versionDate = (v) => {
    const m = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})/.exec(String(v || ''));
    return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : String(v || '');
};

// Notes typed with arrow keys before release-test.sh used `read -e` carry raw
// terminal codes (ESC[A, ESC[C…, sometimes without the ESC): not text.
const cleanNote = (s) => String(s || '')
    // eslint-disable-next-line no-control-regex
    .replace(/\u001b\[[0-9;?]*[ -/]*[@-~]|\u001b[@-_]?|[\u0000-\u0008\u000b-\u001f\u007f]/g, '')
    .replace(/(?:\[[ABCDHF]){2,}/g, '')
    .trim();

/**
 * Install a build from the TEST channel on the selected webshop (SoftTouch
 * SUPER only — auth checks it again server-side). Builds are published with
 * tools/release-test.sh in the storefront repo. Whether a build fits is shown
 * here, but the shop's own updater decides: it only installs a build whose
 * host list holds the shop and whose client matches the one it serves, so a
 * live shop never takes a test build.
 */
const TestVersionDialog = ({ open, onClose, t, api, login, webshop, notify }) => {
    const tr = (k, fb) => (typeof t === 'function' ? t(k, fb) : (fb && typeof fb === 'object' ? fb.defaultValue : fb));
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [installing, setInstalling] = useState(null);   // version being installed
    const [result, setResult] = useState(null);
    const [confirmSwitch, setConfirmSwitch] = useState(null);   // entry of a client switch to confirm

    const base = { key: api, webshop_id: webshop?.id, sft_user_id: login?.sft_user_id };
    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const r = await SftGlobalData.post('webshop&action=testVersions', base);
            if (r && r.success) setInfo(r.data);
            else setError(r?.message || tr('cfg_act_testver_load_failed', 'Could not read the test channel.'));
        } catch (e) {
            setError(e?.message || tr('cfg_act_testver_load_failed', 'Could not read the test channel.'));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, webshop?.id, login?.sft_user_id]);

    useEffect(() => {
        if (!open || !webshop) return;
        setResult(null);
        load();
    }, [open, webshop, load]);

    const install = async (version) => {
        setInstalling(version);
        setResult(null);
        try {
            const r = await SftGlobalData.post('webshop&action=update', { ...base, channel: 'test', version });
            // a failure comes back via ResponseHandler::error with the step log in data/dev
            const payload = [r?.data, r?.dev, r?.data?.dev].find(p => p && Array.isArray(p.steps));
            const res = payload || { ok: false, steps: [{ step: 'error', msg: r?.message || tr('cfg_act_failed', 'failed'), ok: false }] };
            setResult({ version, ...res });
            if (notify) notify(res.ok
                ? tr('cfg_act_testver_installed', 'Test version installed.')
                : tr('cfg_act_testver_install_failed', 'Test version not installed — see the log.'), res.ok ? 2 : 1);
            await load();
        } catch (e) {
            setResult({ version, ok: false, steps: [{ step: 'error', msg: e?.message || tr('cfg_act_failed', 'failed'), ok: false }] });
        } finally {
            setInstalling(null);
        }
    };

    const reason = (e) => {
        if (!e.host_ok) return tr('cfg_act_testver_not_for_shop', 'not released for this webshop');
        if (!e.client_ok && e.client_switch) {
            return tr('cfg_act_testver_switch_reason', { from: info?.client || '?', to: e.client || '?', defaultValue: 'switches this webshop from the {{from}} to the {{to}} client — only if its config.php allows it' });
        }
        if (!e.client_ok) return tr('cfg_act_testver_other_client', 'ships another client than this webshop serves');
        if (!e.newer) return tr('cfg_act_testver_not_newer', 'not newer than the current version');
        return '';
    };
    const entries = (info && info.entries) || [];

    return (
        <Dialog open={open} onClose={installing ? undefined : onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }} data-testver-dialog>
            <DialogTitle>
                <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>{tr('cfg_act_testver_title', 'Install test version')}</Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Alert severity="info">
                        {tr('cfg_act_testver_info', 'Builds from the test channel, published with tools/release-test.sh. The webshop only installs a build released for its own domain and with the client it serves; a live shop never takes a test build. Then migrations, translations and statics run as with a regular update.')}
                    </Alert>
                    {loading && <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><CircularProgress size={16} /><Typography variant="body2">{tr('cfg_act_testver_loading', 'Reading the test channel…')}</Typography></Box>}
                    {error && <Alert severity="error">{error}</Alert>}
                    {info && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }} data-testver-current>
                            {tr('cfg_act_testver_current', 'This webshop')}: <strong>{info.host || '?'}</strong>
                            {' · '}{tr('cfg_act_testver_version', 'version')} <strong>{info.current ? versionDate(info.current) : '?'}</strong>
                            {info.channel === 'test' && <Chip size="small" color="warning" label={tr('cfg_act_testver_chip_test', 'test')} sx={{ ml: 0.75, height: 18 }} />}
                            {' · '}{tr('cfg_act_testver_client', 'client')} <strong>{info.client || '?'}</strong>
                        </Typography>
                    )}
                    {info && info.token_missing && (
                        <Alert severity="error">{tr('cfg_act_testver_token_missing', 'Auth cannot read the test channel: STLIB_WEBSHOP_TEST_CHANNEL_TOKEN is missing in the STLIB credentials.')}</Alert>
                    )}
                    {info && !info.token_missing && !entries.length && !loading && (
                        <Alert severity="info">{info.feed_code === 404 || !info.feed_code
                            ? tr('cfg_act_testver_no_feed', 'There is no test channel yet (publish a build with tools/release-test.sh).')
                            : tr('cfg_act_testver_empty', 'The test channel holds no builds.')}</Alert>
                    )}
                    {entries.map(e => (
                        <Box key={e.version} data-testver-entry={e.version} sx={{
                            border: '1px solid', borderColor: e.installable ? 'success.light' : (e.switchable ? 'warning.light' : 'divider'), borderRadius: 1.5, p: 1.5,
                            display: 'flex', gap: 1.5, alignItems: 'center', opacity: e.installable || e.switchable ? 1 : 0.75,
                        }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{versionDate(e.version)}</Typography>
                                    {(e.parts || []).includes('backend') && <Chip size="small" label={tr('cfg_act_testver_part_backend', 'backend + mini-api')} />}
                                    {(e.parts || []).includes('mini-api') && <Chip size="small" label={tr('cfg_act_testver_part_miniapi', 'mini-api only')} />}
                                    {(e.parts || []).includes('client') && <Chip size="small" color="primary" variant="outlined" label={`${tr('cfg_act_testver_part_client', 'client')} ${e.client || ''}`} />}
                                    {e.git && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{e.git.branch}@{e.git.commit}{e.git.dirty ? ` + ${tr('cfg_act_testver_dirty', 'local changes')}` : ''}</Typography>}
                                </Box>
                                {cleanNote(e.note) && <Typography variant="body2" sx={{ mt: 0.25 }}>{cleanNote(e.note)}</Typography>}
                                <Typography variant="caption" sx={{ color: e.installable ? 'success.main' : 'text.secondary', display: 'block' }}>
                                    {e.installable ? tr('cfg_act_testver_installable', 'can be installed on this webshop') : reason(e)}
                                    {' · '}{(e.hosts || []).join(', ')}
                                </Typography>
                            </Box>
                            {e.switchable && !e.installable ? (
                                // another client: a separate, confirmed action (the shop's own
                                // config.php still has to allow it — the updater decides)
                                <Button variant="outlined" color="warning" size="small" disabled={!!installing}
                                    onClick={() => setConfirmSwitch(e)} sx={{ textTransform: 'none', flexShrink: 0 }} data-testver-switch={e.version}
                                    startIcon={installing === e.version ? <CircularProgress size={14} color="inherit" /> : null}>
                                    {installing === e.version
                                        ? tr('cfg_act_testver_installing', 'Installing…')
                                        : tr('cfg_act_testver_switch', { to: e.client, defaultValue: 'Switch to {{to}}…' })}
                                </Button>
                            ) : (
                                <Button variant="contained" color="warning" size="small" disabled={!e.installable || !!installing}
                                    onClick={() => install(e.version)} sx={{ textTransform: 'none', flexShrink: 0 }}
                                    startIcon={installing === e.version ? <CircularProgress size={14} color="inherit" /> : null}>
                                    {installing === e.version ? tr('cfg_act_testver_installing', 'Installing…') : tr('cfg_act_testver_install', 'Install')}
                                </Button>
                            )}
                        </Box>
                    ))}
                    {result && (
                        <Box sx={{ border: '1px solid', borderColor: result.ok ? 'success.light' : 'error.light', borderRadius: 1.5, p: 1.5 }} data-testver-result>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {versionDate(result.version)} — {result.ok ? tr('cfg_act_testver_result_ok', 'installed') : tr('cfg_act_testver_result_failed', 'not installed')}
                            </Typography>
                            {(result.steps || []).map((s, i) => (
                                <Box key={i} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', py: 0.25 }}>
                                    {s.ok ? <CheckCircleOutlineIcon color="success" sx={{ fontSize: 16, mt: '2px' }} /> : <ErrorOutlineIcon color="error" sx={{ fontSize: 16, mt: '2px' }} />}
                                    <Typography variant="body2" sx={{ fontSize: '0.82rem', wordBreak: 'break-word' }}>{s.msg}</Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <Dialog open={!!confirmSwitch} onClose={() => setConfirmSwitch(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }} data-testver-switch-confirm>
                <DialogTitle>
                    <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                        {tr('cfg_act_testver_switch_title', { to: confirmSwitch?.client || '?', defaultValue: 'Switch this webshop to the {{to}} client?' })}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        {tr('cfg_act_testver_switch_body', {
                            host: info?.host || '?', from: info?.client || '?', to: confirmSwitch?.client || '?',
                            defaultValue: '{{host}} serves the {{from}} client now. This test version replaces it with the {{to}} client (and installs the rest of the build).',
                        })}
                    </Typography>
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                        {tr('cfg_act_testver_switch_backup', 'First the webshop copies its current client (index.php and the bundles) to .UPDATES/client-backup-…/ on its own server. To go back: RESTORE.txt in that folder.')}
                    </Alert>
                    <Alert severity="warning">
                        {tr('cfg_act_testver_switch_allow', "Only works when the webshop allows it: define('UPDATE_TEST_ALLOW_CLIENT_SWITCH', true); in its server/v1/config/config.php. Never on a live webshop.")}
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setConfirmSwitch(null)} sx={{ textTransform: 'none' }}>{tr('cfg_act_cancel', 'Cancel')}</Button>
                    <Button variant="contained" color="warning" sx={{ textTransform: 'none' }} data-testver-switch-go
                        onClick={() => { const v = confirmSwitch.version; setConfirmSwitch(null); install(v); }}>
                        {tr('cfg_act_testver_switch_go', { to: confirmSwitch?.client || '?', defaultValue: 'Switch to {{to}}' })}
                    </Button>
                </DialogActions>
            </Dialog>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={load} disabled={loading || !!installing} sx={{ textTransform: 'none', mr: 'auto' }}>{tr('cfg_act_testver_refresh', 'Refresh')}</Button>
                <Button onClick={onClose} disabled={!!installing} sx={{ textTransform: 'none' }}>{tr('cfg_act_close', 'Close')}</Button>
            </DialogActions>
        </Dialog>
    );
};

TestVersionDialog.propTypes = {
    open: PropTypes.bool, onClose: PropTypes.func, t: PropTypes.func, api: PropTypes.string,
    login: PropTypes.object, webshop: PropTypes.object, notify: PropTypes.func,
};

export default TestVersionDialog;
